import React from 'react';
import {
  ChevronDown,
  ChevronUp,
  Settings2,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { FilterItem } from '../types';
import { coerceFilterBoolean } from '../filterBoolean';

// Import control components
import NumberInput from './NumberInput';
import ToggleSwitch from './ToggleSwitch';
import CardCheckboxGroup from './CardCheckboxGroup';
import CardRadioGroup from './CardRadioGroup';
import SelectInput from './SelectInput';

export interface ToggleWithDetailProps {
  filter: FilterItem;
  value: boolean;
  onToggle: (enabled: boolean) => void;
  configValues: Record<string, any>;
  onConfigChange: (id: string, value: any) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  /** Sets expanded state explicitly (used to collapse when leaving Custom strategy). */
  onSetExpanded?: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function ToggleWithDetail({
  filter,
  value: enabled,
  onToggle,
  configValues,
  onConfigChange,
  isExpanded,
  onToggleExpand,
  onSetExpanded,
  disabled = false,
  className,
}: ToggleWithDetailProps): React.ReactElement {
  // Get current strategy value
  const strategyKey = filter.strategyKey;
  const currentStrategy = strategyKey
    ? configValues[strategyKey] || 'balanced'
    : null;
  const hasStrategy =
    strategyKey && filter.strategyOptions && filter.strategyOptions.length > 0;

  // Handle strategy change
  const handleStrategyChange = (newStrategy: string) => {
    if (!strategyKey || !filter.strategyPresets) return;

    // Apply preset values for the selected strategy
    const preset = filter.strategyPresets[newStrategy];
    if (preset) {
      Object.entries(preset).forEach(([key, val]) => {
        const fullKey = `${filter.configKey}.${key}`;
        onConfigChange(fullKey, val);
      });
    }

    // Set the strategy value
    onConfigChange(strategyKey, newStrategy);

    if (newStrategy === 'custom') {
      onSetExpanded?.(true);
    } else {
      onSetExpanded?.(false);
    }
  };

  // Preset strategies: only show the parameter grid when "Custom" is selected and expanded.
  // Filters without a strategy selector keep the previous expand/collapse behavior.
  const showConfig =
    enabled &&
    Boolean(filter.children?.length) &&
    (hasStrategy ? currentStrategy === 'custom' && isExpanded : isExpanded);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Main toggle row */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          {/* Description omitted - FilterFieldShell renders it via the (i)
              tooltip so each toggle row stays compact and avoids the
              duplicate label/sublabel/(i)-block stack we had previously. */}
          <ToggleSwitch
            checked={enabled}
            onChange={onToggle}
            {...(filter.label !== undefined && { label: filter.label })}
            disabled={disabled}
            testId={filter.id}
          />
        </div>

        {enabled && hasStrategy && (
          <div className="flex items-center gap-2">
            {/* Strategy selector */}
            <div className="flex items-center gap-1">
              <SlidersHorizontal className="w-4 h-4 text-text-muted" />
              <select
                value={currentStrategy || 'balanced'}
                onChange={(e) => handleStrategyChange(e.target.value)}
                disabled={disabled}
                aria-label={`${filter.label} strategy preset`}
                className={cn(
                  'text-xs px-2 py-1 rounded border border-border-subtle',
                  'bg-surface-container-low text-text-primary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {filter.strategyOptions?.map((strategy) => (
                  <option key={strategy} value={strategy}>
                    {strategy.charAt(0).toUpperCase() + strategy.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Config toggle: only for Custom - presets hide the grid entirely */}
            {currentStrategy === 'custom' &&
              filter.children &&
              filter.children.length > 0 && (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  className={cn(
                    'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium',
                    'text-text-muted hover:text-text-primary hover:bg-surface-container',
                    'transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary'
                  )}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5" />
                      <span>Less</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5" />
                      <span>Config</span>
                    </>
                  )}
                </button>
              )}
          </div>
        )}

        {/* Show Config button for filters without strategy */}
        {enabled &&
          !hasStrategy &&
          filter.children &&
          filter.children.length > 0 && (
            <button
              type="button"
              onClick={onToggleExpand}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium',
                'text-text-muted hover:text-text-primary hover:bg-surface-container',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary'
              )}
            >
              <Settings2 className="w-3.5 h-3.5" />
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  <span>Less</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  <span>Config</span>
                </>
              )}
            </button>
          )}
      </div>

      {/* Strategy indicator for non-custom strategies */}
      {enabled &&
        hasStrategy &&
        currentStrategy &&
        currentStrategy !== 'custom' && (
          <div className="flex items-center gap-2 text-xs text-text-muted pl-1">
            <span className="px-2 py-0.5 rounded bg-accent-primary/10 text-accent-primary font-medium">
              {currentStrategy.charAt(0).toUpperCase() +
                currentStrategy.slice(1)}{' '}
              preset
            </span>
            <span>Choose Custom to edit parameters.</span>
          </div>
        )}

      {/* Expanded config panel */}
      {showConfig && filter.children && filter.children.length > 0 && (
        <div className="flex flex-col gap-4 p-4 rounded-lg bg-surface-container border border-border-subtle">
          <div className="flex items-center gap-2 pb-2 border-b border-border-subtle">
            <Settings2 className="w-4 h-4 text-text-muted" />
            <span className="text-sm font-medium text-text-secondary">
              {hasStrategy ? 'Custom configuration' : 'Configuration'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {filter.children.map((childFilter) => {
              // Get the value from configValues using the full key
              const fullKey = childFilter.id;
              const rawChild = configValues[fullKey];
              const childValue =
                childFilter.type === 'toggle'
                  ? coerceFilterBoolean(
                      rawChild,
                      Boolean(childFilter.defaultValue)
                    )
                  : (rawChild ?? childFilter.defaultValue);
              const isDisabled = Boolean(disabled || !enabled);

              return (
                <div key={childFilter.id} className="col-span-1">
                  {childFilter.type === 'card-checkbox-group' ? (
                    <CardCheckboxGroup
                      value={childValue}
                      onChange={(val) => onConfigChange(fullKey, val)}
                      options={childFilter.options || []}
                      label={childFilter.label}
                      hasSelectAll={childFilter.hasSelectAll ?? false}
                      disabled={isDisabled}
                    />
                  ) : childFilter.type === 'card-radio-group' ? (
                    <CardRadioGroup
                      value={childValue}
                      onChange={(val) => onConfigChange(fullKey, val)}
                      options={childFilter.options || []}
                      label={childFilter.label}
                      disabled={isDisabled}
                    />
                  ) : childFilter.type === 'number' ? (
                    <NumberInput
                      value={childValue}
                      onChange={(val) => onConfigChange(fullKey, val)}
                      min={childFilter.min ?? 0}
                      max={childFilter.max ?? 100}
                      step={childFilter.step ?? 1}
                      {...(childFilter.prefix !== undefined && {
                        prefix: childFilter.prefix,
                      })}
                      {...(childFilter.suffix !== undefined && {
                        suffix: childFilter.suffix,
                      })}
                      label={childFilter.label}
                      disabled={isDisabled}
                    />
                  ) : childFilter.type === 'toggle' ? (
                    <ToggleSwitch
                      checked={childValue}
                      onChange={(val) => onConfigChange(fullKey, val)}
                      label={childFilter.label}
                      disabled={isDisabled}
                    />
                  ) : childFilter.type === 'select' ? (
                    <SelectInput
                      value={childValue}
                      onChange={(val) => onConfigChange(fullKey, val)}
                      options={childFilter.options || []}
                      label={childFilter.label}
                      disabled={isDisabled}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ToggleWithDetail;
